// The client log (fault-reports.md §4.4, R1627).
//
// A player report carries the end of the client log: the last lines this
// page wrote to the console, and its uncaught faults. Before this module
// the client kept no log of its own; the console was the only record, and
// a page cannot read the console back. `installClientLog` wraps the five
// console methods once, so every line still reaches the console, and a
// copy lands in a ring of `REPORT_SHEET.logLines` lines, each cut to
// `logLineRunes`.
//
// **The log holds no other player's chat.** No module writes a chat line
// to the console today, and the rule must not depend on that staying so.
// The game page names the chat of other players (`setChatSource`), and the
// log drops every line that holds one of those texts: when the line is
// written, and again when a report reads the tail, because a chat line can
// arrive after the log line that quotes it. A chat text under
// `chatFilterMinRunes` is not matched: a word like "ok" names nothing and
// would drop most lines. A dropped line leaves no trace in the tail.
//
// The module has no effect on import. `net/faultboot.ts` installs it beside
// the error report handlers, so the log starts as early as they do.

import { cutRunes, runeCount } from '../util/text';
import { REPORT_SHEET } from '../gen/presentation';

/** The console methods the log copies. */
const LEVELS = ['log', 'info', 'warn', 'error', 'debug'] as const;
type Level = (typeof LEVELS)[number];

/** One argument of a console call, as text. It never throws. */
export function argText(v: unknown): string {
  try {
    if (typeof v === 'string') return v;
    if (v instanceof Error) return `${v.name}: ${v.message}`;
    if (v === null || v === undefined || typeof v !== 'object') return String(v);
    return JSON.stringify(v) ?? String(v);
  } catch {
    return '[?]';
  }
}

/** The tail of the log a report carries. */
export interface LogTail {
  text: string;
  lines: number;
  /** The size of the text in UTF-8 bytes. */
  bytes: number;
}

export class ClientLog {
  private readonly lines: string[] = [];
  private chat: () => readonly string[] = () => [];
  private readonly t0: number;

  constructor(
    private readonly max: number = REPORT_SHEET.logLines,
    private readonly lineRunes: number = REPORT_SHEET.logLineRunes,
    private readonly now: () => number = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
  ) {
    this.t0 = this.now();
  }

  /** Names the chat texts of other players, which the log must not hold. */
  setChatSource(fn: () => readonly string[]): void { this.chat = fn; }

  /** Adds one line: the seconds since the page started, the level and the
   *  words. A line that holds another player's chat is dropped. */
  add(level: string, args: readonly unknown[]): void {
    const sec = ((this.now() - this.t0) / 1000).toFixed(3);
    const line = cutRunes(`+${sec}s ${level} ${args.map(argText).join(' ')}`.replace(/\s+/g, ' '), this.lineRunes);
    if (holdsChat(line, this.chat())) return;
    this.lines.push(line);
    if (this.lines.length > this.max) this.lines.splice(0, this.lines.length - this.max);
  }

  /** The tail a report carries, read through the chat filter again. */
  tail(): LogTail {
    const chat = this.chat();
    const kept = this.lines.filter((l) => !holdsChat(l, chat));
    const text = kept.join('\n');
    return { text, lines: kept.length, bytes: new TextEncoder().encode(text).length };
  }

  /** How many lines the ring holds now. */
  get size(): number { return this.lines.length; }
}

/** Whether a log line holds one of the chat texts (R1627). */
export function holdsChat(line: string, chat: readonly string[]): boolean {
  const low = line.toLowerCase();
  for (const c of chat) {
    const t = c.trim().toLowerCase();
    if (runeCount(t) < REPORT_SHEET.chatFilterMinRunes) continue;
    if (low.includes(t)) return true;
  }
  return false;
}

let installed: ClientLog | null = null;

/** The page's client log, or null before `installClientLog`. */
export function clientLog(): ClientLog | null { return installed; }

/**
 * Wraps the five console methods and the two fault events once, and
 * answers the log. A second call answers the first log. It never throws.
 */
export function installClientLog(): ClientLog | null {
  if (installed) return installed;
  try {
    const log = new ClientLog();
    for (const level of LEVELS) {
      const own = console[level] as (...args: unknown[]) => void;
      if (typeof own !== 'function') continue;
      console[level as Level] = (...args: unknown[]) => {
        try { log.add(level, args); } catch { /* the console still gets the line */ }
        own.apply(console, args);
      };
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (e) => log.add('fault', [e.error ?? e.message]));
      window.addEventListener('unhandledrejection', (e) => log.add('rejection', [e.reason]));
    }
    installed = log;
  } catch { /* the page runs without a log */ }
  return installed;
}
